import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Icon } from '@iconify/react'

interface TopbarIdentity {
  label: string
  ariaLabel: string
  icon: string
}

interface TopbarTheme {
  label: string
  icon: string
}

interface TopbarActionsProps {
  running: boolean
  syncStatus: string
  syncLabel: string
  buildAction?: ReactNode
  account: TopbarIdentity
  theme: TopbarTheme
  onAccount: () => void
  onTheme: () => void
  onAchievements: () => void
  onGlossary: () => void
  onChangelog: () => void
  onRestart: () => void
}

export function TopbarActions({
  running,
  syncStatus,
  syncLabel,
  buildAction,
  account,
  theme,
  onAccount,
  onTheme,
  onAchievements,
  onGlossary,
  onChangelog,
  onRestart,
}: TopbarActionsProps) {
  const [moreOpen, setMoreOpen] = useState(false)
  const moreRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!moreOpen) return
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!moreRef.current?.contains(event.target as Node)) setMoreOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMoreOpen(false)
    }
    document.addEventListener('pointerdown', closeOnOutsidePress)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePress)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [moreOpen])

  const runAndClose = (action: () => void) => () => {
    setMoreOpen(false)
    action()
  }

  return <div className="topbar__actions">
    {running && <span className={`save-status save-status--${syncStatus}`}>● {syncLabel}</span>}
    {buildAction}
    <button className="text-button" type="button" onClick={onAccount} aria-label={account.ariaLabel}>
      <Icon icon={account.icon} /> <span className="account-label">{account.label}</span>
    </button>
    <div className="topbar-secondary-actions">
      <button className="text-button" type="button" onClick={onTheme} aria-label={`Tema: ${theme.label}. Cambiar tema`}><Icon icon={theme.icon} /> <span>{theme.label}</span></button>
      <button className="text-button" type="button" onClick={onAchievements} aria-label="Ver logros de carrera"><Icon icon="mdi:trophy-outline" /> <span>Logros</span></button>
      <button className="text-button" type="button" onClick={onGlossary} aria-label="Abrir glosario de la build"><Icon icon="mdi:book-open-page-variant-outline" /> <span>Glosario</span></button>
      <button className="text-button" type="button" onClick={onChangelog} aria-label="Ver notas de las versiones"><Icon icon="mdi:notebook-outline" /> <span>Novedades</span></button>
      {running && <button className="text-button text-button--danger" type="button" aria-label="Reiniciar la run actual" onClick={onRestart}><Icon icon="mdi:restart" /> <span>Reiniciar run</span></button>}
    </div>
    <div ref={moreRef} className={`topbar-more${moreOpen ? ' is-open' : ''}`}>
      <button className="text-button topbar-more__trigger" type="button" aria-label={moreOpen ? 'Cerrar más opciones' : 'Abrir más opciones'} aria-expanded={moreOpen} aria-controls="topbar-more-menu" onClick={() => setMoreOpen((current) => !current)}>
        <Icon icon={moreOpen ? 'mdi:close' : 'mdi:dots-horizontal'} /><span>Más</span>
      </button>
      {moreOpen && <div id="topbar-more-menu" className="topbar-more__menu" role="group" aria-label="Más opciones">
        <button type="button" onClick={runAndClose(onTheme)} aria-label={`Tema: ${theme.label}. Cambiar tema`}><Icon icon={theme.icon} /><span><strong>Tema</strong><small>{theme.label}</small></span></button>
        <button type="button" onClick={runAndClose(onAchievements)} aria-label="Ver logros de carrera"><Icon icon="mdi:trophy-outline" /><span>Logros</span></button>
        <button type="button" onClick={runAndClose(onGlossary)} aria-label="Abrir glosario de la build"><Icon icon="mdi:book-open-page-variant-outline" /><span>Glosario</span></button>
        <button type="button" onClick={runAndClose(onChangelog)} aria-label="Ver notas de las versiones"><Icon icon="mdi:notebook-outline" /><span>Novedades</span></button>
        {running && <div className="topbar-more__danger"><button type="button" onClick={runAndClose(onRestart)} aria-label="Reiniciar la run actual"><Icon icon="mdi:restart" /><span>Reiniciar run</span></button></div>}
      </div>}
    </div>
  </div>
}
